import React, { useEffect, useMemo, useState } from 'react';
import { Bug, ChevronDown, ChevronRight, ExternalLink, LayoutGrid, List, RefreshCw, Sparkles, X } from 'lucide-react';
import integrationsService from '../../services/integrationsService';
import IntegrationDesigner from '../IntegrationDesigner';
import { formatDateTime } from '../../utils/datetime';
import ConfirmModal from './ConfirmModal';
import CustomCodeAssistant from '../CustomCodeAssistant';
import '../PartnerSyncTemplates.css';        // .cca — the assistant chat
import CopyButton from './CopyButton';
import { formatMessage } from './messageLogUtils';
import Spinner from './Spinner';
import './IntegrationLogTable.css';
import './Disclosure.css';                 // .hc-chevron-btn — the bordered row chevron

// THE INTEGRATION LOG, AS A TABLE — one row per pipeline step (integration-log-*), newest first.
//
// SHARED by Integrate Systems > Logs (every integration) and Universal API > Logs (only the steps that
// involve the Universal API, for the selected hotel), so a step reads the same on both screens.
//
// A ROW EXPANDS TO ITS MESSAGE'S ROUND TRIP: every step carrying the same transaction id — the proxy's and,
// joined on the server by correlation id, the Universal API's own — laid out as the request going out and
// the response coming back, each step opening its detail and (redacted) body in a dialog.

// What a step name means, in words. Unknown steps fall back to the raw name.
const STEP_LABEL = {
    'oxi.push.received': 'OXI push received',
    'oxi.push.refused': 'Push refused',
    'oxi.push.answered': 'Answered OXI',
    'proxy.route': 'Route',
    'proxy.route.match': 'Route',
    'proxy.convert': 'Convert',
    'proxy.uapi.request': 'Call Universal API',
    'proxy.uapi.response': 'Universal API answer',
    'proxy.uapi.retry': 'Universal API retry',
    'oxi.poll.delivered': 'Delivered to OXI',
    'oxi.poll.map-failed': 'Mapping failed',
    'oxi.poll.refused': 'Poll refused',
    'uapi.oxi.queued': 'Queued for OXI',
    'uapi.arrived': 'Arrived',
    'uapi.authorized': 'Authorised',
    'uapi.resolved': 'x-hotelid resolved',
    'uapi.granted': 'Granted',
    'uapi.redaction': 'Redaction',
    'uapi.backend': 'Backend call',
    'uapi.responded': 'Responded',
    'uapi.denied': 'Refused',
    'test.push.sent': 'Test push sent',
    'test.push.answered': 'Test push answered',
    'test.poll.sent': 'Test poll sent',
    'test.poll.answered': 'Test poll answered',
    'poller.event': 'Polled from OHIP',
    'poller.tick': 'Polled OHIP',
    'poller.start': 'Polling OHIP',
    'resync.tick': 'Resynced from OHIP',
    'resync.start': 'Resyncing from OHIP',
    'scheduledjob.tick': 'Scheduled job',
    // written before steps carried stage and lane
    'oxi.push.queued': 'Queued',
    'oxi.push.control': 'Control message',
    'oxi.push.failed': 'Queueing failed',
    'outbound.no-route': 'No route',
    'outbound.route.resolved': 'Route resolved',
    'outbound.forward': 'Forward',
    'test.push': 'Test push',
    'test.poll': 'Test poll',
};
export const stepLabel = (step) => STEP_LABEL[step] || step || '—';

const OUTCOME_CLASS = { ok: 'ilt-ok', warning: 'ilt-warning', skipped: 'ilt-skipped', denied: 'ilt-denied', error: 'ilt-error' };

const Outcome = ({ outcome }) => (
    <span className={`ilt-chip ${OUTCOME_CLASS[outcome] || 'ilt-unknown'}`}>{outcome || 'unknown'}</span>
);

// JSON AND XML BOTH, READ AS A HUMAN WANTS THEM. formatMessage sniffs which it is and re-indents it, and
// hands back the original when it will not parse — the payload somebody is squinting at is usually the
// malformed one, and "could not format" would remove the only evidence of what went wrong. Shared with the
// connector message logs rather than a second formatter here.
const pretty = (text) => (text ? formatMessage(String(text)) : '');

// ── one step, in a dialog ────────────────────────────────────────────────────
// Opened from a step in the pipeline. Everything the step recorded: the sentence, the facts, the detail and
// the (redacted) body — read one at a time, rather than every step's body stacked down the page.
const StepDialog = ({ step, onClose }) => {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const facts = [
        ['When', formatDateTime(step['@timestamp'])], ['Step', step.step], ['Lambda', step.lambda], ['Request', step.request_id],
        ['Integration', step.integration_name || step.integration_id], ['System', step.system], ['Proxy', step.proxy],
        ['Property', step.property], ['Hotel', step.hotel_id], ['Message type', step.message_type],
        ['Transaction', step.transaction_id], ['Route', step.route_name || step.route_id], ['Queue', step.queue], ['HTTP', step.http_status],
        ['Duration', step.duration_ms != null ? `${step.duration_ms} ms` : null], ['Bytes', step.bytes],
        ['From', step.source_ip], ['x-hotelid', step.x_hotelid], ['Credential', step.credential_id],
        ['Target', step.target_system], ['Universal API transaction', step.uapi_transaction_id],
    ].filter(([, v]) => v !== undefined && v !== null && v !== '');

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-lg ilt-dialog" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="ilt-dialog-title">
                <div className="modal-header">
                    <h2 id="ilt-dialog-title">
                        {labelOf(step)} <Outcome outcome={step.outcome} />
                    </h2>
                    <button className="modal-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
                </div>
                <div className="modal-body ilt-dialog-body">
                    <p className="ilt-dialog-sentence">{step.message}</p>
                    <dl className="ilt-facts">
                        {facts.map(([k, v]) => (
                            <React.Fragment key={k}><dt>{k}</dt><dd><code>{String(v)}</code></dd></React.Fragment>
                        ))}
                    </dl>
                    {step.detail && (
                        <section>
                            <h3 className="ilt-dialog-h">Detail</h3>
                            <pre className="ilt-pre">{pretty(step.detail)}</pre>
                        </section>
                    )}
                    {step.payload && (
                        <section>
                            <div className="ilt-dialog-h-row">
                                <h3 className="ilt-dialog-h">Message (redacted)</h3>
                                <CopyButton value={step.payload} label="Copy" />
                            </div>
                            <pre className="ilt-pre ilt-payload">{pretty(step.payload)}</pre>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
};

// WHERE EACH STEP BELONGS: its stage (system, proxy, hub, target) and which way it was travelling
// (request out, response back), both written with the step.
const STAGES = ['system', 'proxy', 'hub', 'integration', 'target'];

// Steps written before stage/lane were recorded are placed by name.
const placeOf = (step) => {
    if (STAGES.includes(step.stage)) return { stage: step.stage, lane: step.lane === 'response' ? 'response' : 'request' };
    const name = step.step || '';
    if (name.startsWith('test.')) return { stage: 'system', lane: step.http_status ? 'response' : 'request' };
    if (name.startsWith('oxi.poll.') || name === 'oxi.push.refused') return { stage: 'proxy', lane: 'response' };
    if (name.startsWith('oxi.')) return { stage: 'proxy', lane: 'request' };
    if (name === 'outbound.forward') return { stage: 'target', lane: 'request' };
    return { stage: 'hub', lane: 'request' };
};

const clock = (ts) => {
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? '' : `${d.toTimeString().slice(0, 8)}.${String(d.getMilliseconds()).padStart(3, '0')}`;
};

// A route decision is named by its route: every route drawn on the proxy gets one, matched or bypassed, and
// the name is the only thing that tells them apart.
const labelOf = (step) => (step.step === 'proxy.route.match' && step.route_name
    ? step.route_name
    : stepLabel(step.step));

// ── one message, painted onto the integration's own design ───────────────────
//
// THE DESIGNER IS THE PICTURE. A record is what happened to ONE message travelling through a design somebody
// drew, so it is shown on that design — the same boards, the same routes, the same targets — with each box
// coloured by what happened there: green where the message got through, grey where that part was bypassed,
// red where it failed, pale where it was never involved. Clicking a box lists the entries behind it.
//
// Drawing a second picture of the same thing (which is what this was) asks the reader to hold two layouts in
// their head and keep them in step; and the two drift the moment either side is changed.

// A STEP THAT CARRIES AN HTTP ANSWER IS AS BAD AS THE ANSWER, whatever outcome it was logged with: the Universal API
// traced its 501s as 'ok' until 2026-10-27, so records from before then still say so.
const outcomeOf = (st) => {
    const status = Number(st.http_status || st.status) || 0;
    if (status >= 500) return 'error';
    if (status >= 400 && st.outcome !== 'error') return 'denied';
    return st.outcome;
};
// A step may colour its BOX differently from its own dot (boardOutcome): see the poller's skip in attribute().
const worstOf = (steps) => ['error', 'denied', 'skipped', 'warning', 'ok'].find(o => steps.some(st => (st.boardOutcome || outcomeOf(st)) === o));
const stateOfSteps = (steps) => {
    if (!steps.length) return null;
    const worst = worstOf(steps);
    if (worst === 'error' || worst === 'denied') return 'error';
    if (worst === 'skipped') return 'skipped';
    // Delivered with a caveat (a linked record left off): the message got through, so the box is ok-shaped,
    // but coloured so the caveat is not lost.
    if (worst === 'warning') return 'warning';
    return 'ok';
};

// A MESSAGE A ROUTE'S CUSTOM CODE ADDED (a checkout's FOLIO beside its reservation) is logged with
// detail.derivedMessage (uapi ohip-enqueue). It gets a row of its own on the board, keyed "<route id>~<type>".
const detailOf = (st) => {
    const d = st && st.detail;
    if (!d) return {};
    if (typeof d === 'object') return d;
    try { return JSON.parse(d); } catch (e) { return {}; }
};
const derivedOf = (st) => detailOf(st).derivedMessage || null;
const DERIVED_SEP = '~';

// THE LANES OF A RECORD, for the Log entries list: one per Universal API module its messages went to (/rsv for the
// reservation, /csh for the folio its route's custom code added). A proxy step names its module (detail.module, or the
// path it called); the Universal API's own trace steps follow the call they belong to by its transaction id. Steps
// that belong to the whole record (received, routed, answered) are not in a lane.
const moduleOfPath = (path) => {
    const m = /^\/([A-Za-z0-9_-]+)\//.exec(String(path || ''));
    return m ? `/${m[1]}` : null;
};
const lanesOf = (steps) => {
    const laneOfProxy = (st) => {
        if (st.source === 'uapi-trace') return null;
        const d = detailOf(st);
        if (!['proxy.convert', 'proxy.uapi.request', 'proxy.uapi.retry', 'proxy.uapi.response', 'proxy.mq.queued'].includes(st.step)) return null;
        return d.module || moduleOfPath(d.path) || null;
    };
    const byTxn = new Map();
    for (const st of steps) {
        const lane = laneOfProxy(st);
        if (lane && st.uapi_transaction_id) byTxn.set(String(st.uapi_transaction_id), lane);
    }
    const laneOf = (st) => (st.source === 'uapi-trace'
        ? (st.uapi_transaction_id ? byTxn.get(String(st.uapi_transaction_id)) || null : null)
        : laneOfProxy(st));
    const lanes = new Map();
    const shared = [];
    for (const st of steps) {
        const lane = laneOf(st);
        if (!lane) { shared.push(st); continue; }
        if (!lanes.has(lane)) lanes.set(lane, { module: lane, steps: [], type: null });
        const l = lanes.get(lane);
        l.steps.push(st);
        if (!l.type && st.message_type) l.type = derivedOf(st) || st.message_type;
    }
    return { lanes: [...lanes.values()], shared };
};

/**
 * Which box each of a record's steps belongs to.
 *
 * The proxy's own steps carry their route (route_id); the Universal API's own trace steps carry no route at
 * all, so they are tied back to one by the transaction id the proxy recorded when it made the call.
 */
const attribute = (steps, graph) => {
    // IDS ARE REASSIGNED EVERY TIME A DESIGN IS SAVED (the nodes are rewritten and the routes cascade), so a
    // record written last week names routes this board no longer has. The NAME is what survives that, and it
    // is what the record was logged with (V491) — so an id that is still on the board wins, and a name is the
    // fallback. Without this, an old record paints nothing at all and the board reads as "never touched".
    const graphRoutes = (graph && graph.routes) || [];
    const graphNodes = (graph && graph.nodes) || [];
    const hasRoute = (id) => graphRoutes.some(r => String(r.id) === String(id));
    const routeByName = (name) => graphRoutes.find(r => r.name && name && String(r.name) === String(name));
    const firstSource = graphNodes.find(n => n.role === 'SOURCE');
    const routeIdOf = (st) => {
        if (st.route_id && hasRoute(st.route_id)) return String(st.route_id);
        const named = routeByName(st.route_name);
        if (named) return String(named.id);
        return st.route_id ? String(st.route_id) : null;
    };
    const nodeIdOf = (st) => {
        if (st.node_id && graphNodes.some(n => String(n.id) === String(st.node_id))) return String(st.node_id);
        return firstSource ? String(firstSource.id) : (st.node_id ? String(st.node_id) : null);
    };

    // The row a proxy step belongs to: its route's, or — for a message the custom code added — that message's own.
    const rowOf = (st) => {
        const id = routeIdOf(st);
        const derived = derivedOf(st);
        return id && derived ? `${id}${DERIVED_SEP}${derived}` : id;
    };
    const byUapiTxn = new Map();
    for (const st of steps) {
        if (st.route_id && st.uapi_transaction_id) byUapiTxn.set(String(st.uapi_transaction_id), rowOf(st));
    }
    const onlyRoute = [...new Set(steps.filter(st => st.route_id).map(routeIdOf).filter(Boolean))];
    const routeOfTrace = (st) => byUapiTxn.get(String(st.uapi_transaction_id))
        || (onlyRoute.length === 1 ? onlyRoute[0] : null);

    const box = (st) => {
        const place = placeOf(st);
        if (st.source === 'uapi-trace') {
            const kind = place.stage === 'target' ? 'target' : place.stage === 'integration' ? 'integration' : 'hub';
            return { kind, id: routeOfTrace(st) };
        }
        if (place.stage === 'system') return { kind: 'system', id: nodeIdOf(st) };
        if (st.route_id) return { kind: 'route', id: rowOf(st) };
        if (place.stage === 'target') return { kind: 'target', id: null };
        return { kind: 'proxy', id: nodeIdOf(st) };
    };

    const map = new Map();   // "kind:id" -> steps
    const put = (kind, id, st) => {
        const key = `${kind}:${id ?? ''}`;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(st);
    };
    for (const st of steps) {
        // A POLLED EVENT THE POLLER DECIDED NOT TO SEND (OHIP had no record for it, a past-dated SUMMARY TOTALS…):
        // OHIP handed it over and the OHIP-Adapter's poller weighed it — both did their job, so both boxes are green;
        // the step keeps its own amber "skipped" and its sentence says why. Everything after them was never called.
        if (st.step === 'poller.event' && st.outcome === 'skipped') {
            put('system', nodeIdOf(st), { ...st, boardOutcome: 'ok' });
            put('proxy', nodeIdOf(st), { ...st, boardOutcome: 'ok' });
            continue;
        }
        const { kind, id } = box(st);
        put(kind, id, st);
    }
    return map;
};

// ── ONE ROUTE'S LEG, BOX BY BOX (2026-10-27) ─────────────────────────────────────────────────────────────────────
//
// A route crosses four boxes — Source (the proxy's route), Universal API, Integration, Target — and each is coloured by
// what happened IN IT, read from the proxy's own steps for the route (they carry the Universal API's answer):
//
//   message prepared and sent                       Source green
//   refused at the Universal API (401/403, no answer)          Universal API red;  Integration, Target untouched
//   the integration has no code (501 not_implemented)          Universal API green; Integration red; Target untouched
//   custom code chose not to send it ({ skipped: true })       … Integration green; Target grey (not sent)
//   queued for a vendor that polls /mq (Thynk)                  … Integration green; Target grey (waiting to be collected)
//   the target answered 2xx                                     all green
//   the target answered an error                                … Integration green; Target red
//
// It used to take the worst of every step on the route for the Source box (a Dailypoint 501 made the SOURCE red) and to
// paint Integration and Target as one bucket from the Universal API's "routing to …" step — green even when Pegasus was
// never called.
const ANSWER_STEPS = new Set(['proxy.uapi.response', 'proxy.mq.queued']);
const LEG_KINDS = ['route', 'hub', 'integration', 'target'];
const bodyOf = (st) => {
    const raw = st && st.payload;
    if (!raw) return {};
    if (typeof raw === 'object') return raw;
    try { return JSON.parse(raw); } catch (e) { return {}; }
};
const legOf = (routeSteps) => {
    const source = stateOfSteps(routeSteps.filter(st => !ANSWER_STEPS.has(st.step)));
    const leg = { route: source, hub: null, integration: null, target: null };
    if (source === 'error' || source === 'skipped') return leg;   // never left the source
    const queued = routeSteps.find(st => st.step === 'proxy.mq.queued');
    if (queued) {
        if (queued.outcome === 'ok') Object.assign(leg, { hub: 'ok', integration: 'ok', target: 'skipped' });
        else Object.assign(leg, { hub: 'ok', integration: queued.outcome === 'skipped' ? 'skipped' : 'error' });
        return leg;
    }
    const answer = routeSteps.find(st => st.step === 'proxy.uapi.response');
    if (!answer) return leg;
    const status = Number(answer.http_status) || 0;
    const body = bodyOf(answer);
    if (!status || status === 401 || status === 403) return { ...leg, hub: 'error' };
    if (status === 501 && body.error === 'not_implemented') return { ...leg, hub: 'ok', integration: 'error' };
    if (status < 300 && body.skipped === true) return { ...leg, hub: 'ok', integration: 'ok', target: 'skipped' };
    if (status < 300) return { ...leg, hub: 'ok', integration: 'ok', target: 'ok' };
    return { ...leg, hub: 'ok', integration: 'ok', target: 'error' };
};

/** The entries behind one box, listed; each opens in full. */
const BoxSteps = ({ title, steps, onOpen, onClose }) => {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-md ilt-dialog" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
                <div className="modal-header">
                    <h2>{title}</h2>
                    <button className="modal-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
                </div>
                <div className="modal-body ilt-dialog-body">
                    <ul className="ilt-boxsteps">
                        {steps.map(st => (
                            <li key={st.id}>
                                <button type="button" className={`ilt-boxstep ilt-outcome-${st.outcome || 'unknown'}`} onClick={() => onOpen(st)}>
                                    <span className="ilt-dot" aria-hidden="true" />
                                    <span className="ilt-boxstep-text">
                                        <span className="ilt-boxstep-label">{labelOf(st)}</span>
                                        <span className="ilt-boxstep-msg">{st.message}</span>
                                    </span>
                                    <span className="ilt-stage-step-time">{clock(st['@timestamp'])}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};

const BOX_TITLE = { system: 'System', proxy: 'Proxy', route: 'Route', hub: 'Universal API', integration: 'Integration', target: 'Target' };

/** The record on its integration's design. Falls back to a plain list when the design cannot be loaded. */
const RecordBoard = ({ steps, graph, onOpen, view = 'board' }) => {
    const [openBox, setOpenBox] = useState(null);
    const boxes = useMemo(() => attribute(steps, graph), [steps, graph]);
    const stepsAt = (kind, id) => boxes.get(`${kind}:${id ?? ''}`) || [];

    // Per route: the leg, and its proxy steps (what the Integration box lists — it has no steps of its own).
    const legs = useMemo(() => new Map([...boxes.entries()]
        .filter(([key]) => key.startsWith('route:'))
        .map(([key, list]) => [key.slice('route:'.length), { leg: legOf(list), steps: list }])), [boxes]);

    // WHAT A BOX LISTS. The Integration box: the integration's own log (its lines and target calls, from the Universal
    // API's trace) and the answer the proxy got. The Target box: the same log — the calls to the target are in it — with
    // its own steps and the answer. Everything else: the steps placed in it.
    const answerOf = (id) => ((legs.get(String(id ?? '')) || {}).steps || []).filter(st => ANSWER_STEPS.has(st.step));
    const boxSteps = (kind, id) => {
        if (kind === 'integration') return [...stepsAt('integration', id), ...answerOf(id)];
        if (kind === 'target') return [...stepsAt('target', id), ...stepsAt('integration', id), ...answerOf(id)];
        return stepsAt(kind, id);
    };

    const run = useMemo(() => ({
        stateOf: (kind, id) => {
            const l = LEG_KINDS.includes(kind) && legs.get(String(id ?? ''));
            if (l) return l.leg[kind];
            return stateOfSteps(stepsAt(kind, id));
        },
        countOf: (kind, id) => boxSteps(kind, id).length,
        onOpen: (kind, id) => setOpenBox({ kind, id }),
        // stepsAt reads `boxes`, so the overlay is rebuilt whenever the record's steps change.
    }), [boxes, legs]);

    // A ROW PER MESSAGE THE CUSTOM CODE ADDED, under the route it came from: a copy of that route, named for the message,
    // carrying the module it went to. Same proxy, so it sits under the same OHIP-Adapter and shares its System box — the
    // message began in the adapter's code, not at the source.
    const board = useMemo(() => {
        if (!graph) return graph;
        const extra = [...boxes.keys()].filter(k => k.startsWith('route:') && k.includes(DERIVED_SEP)).map(k => k.slice('route:'.length));
        if (!extra.length) return graph;
        const routes = [];
        for (const r of graph.routes || []) {
            routes.push(r);
            for (const key of extra.filter(k => k.split(DERIVED_SEP)[0] === String(r.id))) {
                const type = key.split(DERIVED_SEP).slice(1).join(DERIVED_SEP);
                const paths = [...new Set(stepsAt('route', key).map(st => detailOf(st).module).filter(Boolean))];
                routes.push({
                    ...r, id: key, code_route_id: r.id,
                    name: `${type} — added by the route's custom code`,
                    modules: paths.map(p => ({ uapi_route_id: `${key}:${p}`, uapi_route_path: p, name: p })),
                });
            }
        }
        return { ...graph, routes };
    }, [graph, boxes]); // eslint-disable-line

    // THE STEPS AS A LIST — chosen with the Board / Steps toggle, or the only view when there is no design
    // to paint on (the integration may have been deleted since). The steps still tell the story.
    if (!graph || view === 'steps') {
        const item = (st) => (
            <li key={st.id}>
                <button type="button" className={`ilt-boxstep ilt-outcome-${st.outcome || 'unknown'}`} onClick={() => onOpen(st)}>
                    <span className="ilt-dot" aria-hidden="true" />
                    <span className="ilt-boxstep-text">
                        <span className="ilt-boxstep-label">{labelOf(st)}</span>
                        <span className="ilt-boxstep-msg">{st.message}</span>
                    </span>
                    <span className="ilt-stage-step-time">{clock(st['@timestamp'])}</span>
                </button>
            </li>
        );
        // ONE LANE: the steps as they came. SEVERAL (the reservation and the folio its custom code added): what the whole
        // record did before them, each lane's steps together under its module, then what came after.
        const { lanes, shared } = lanesOf(steps);
        if (lanes.length < 2) return <ul className="ilt-boxsteps ilt-boxsteps-flat">{steps.map(item)}</ul>;
        const lastLaned = Math.max(...lanes.flatMap(l => l.steps.map(st => new Date(st['@timestamp']).getTime())));
        const after = shared.filter(st => new Date(st['@timestamp']).getTime() > lastLaned);
        const before = shared.filter(st => !after.includes(st));
        return (
            <div>
                <ul className="ilt-boxsteps ilt-boxsteps-flat">{before.map(item)}</ul>
                {lanes.map(l => (
                    <div key={l.module} className="ilt-lane"
                         style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.5rem 0.6rem', margin: '0.5rem 0' }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#4b5563', margin: '0 0 0.35rem' }}>
                            <code>{l.module}</code>{l.type ? ` — ${l.type}` : ''}
                            <span style={{ fontWeight: 400, color: '#9ca3af' }}> · {l.steps.length} entries</span>
                        </div>
                        <ul className="ilt-boxsteps ilt-boxsteps-flat">{l.steps.map(item)}</ul>
                    </div>
                ))}
                <ul className="ilt-boxsteps ilt-boxsteps-flat">{after.map(item)}</ul>
            </div>
        );
    }

    // The Integration box lists the Universal API's answer for the route — the step that says what the integration did.
    const open = openBox ? boxSteps(openBox.kind, openBox.id) : [];
    // The box says which one it is, so the list can too: the route's name, the system's, the backend's.
    const openTitle = () => {
        if (!openBox) return '';
        const first = open[0] || {};
        const named = { route: first.route_name, system: first.system, proxy: first.proxy, target: first.target_system }[openBox.kind];
        return named ? `${BOX_TITLE[openBox.kind]} — ${named}` : (BOX_TITLE[openBox.kind] || 'Steps');
    };
    return (
        <div className="ilt-board">
            <IntegrationDesigner
                nodes={board.nodes || []}
                systems={board.systems || []}
                routes={board.routes || []}
                customCode={board.customCode || []}
                readOnly
                run={run}
            />
            {openBox && (
                <BoxSteps
                    title={openTitle()}
                    steps={open}
                    onClose={() => setOpenBox(null)}
                    onOpen={(st) => { setOpenBox(null); onOpen(st); }}
                />
            )}
        </div>
    );
};

// WHO THE UNIVERSAL API WAS CALLED AS, AND WHAT THAT CALLER MAY REACH (logs.js credentialsOf).
//
// One line per credential the record's steps name: its name, where its module list comes from — the
// backend's own override, an access profile, or the credential itself (V522), the same order the gateway
// decides in — and every module on it. The module this message needed is marked, and flagged when it is
// not on the list, which is the commonest reason a record stops at the Universal API.
//
// TODAY'S GRANTS, not the ones at the time of the call: grants are not versioned. Said on the panel, so a
// record from last week is not read as proof of what was allowed then.
const SOURCE_TEXT = {
    access: 'set on the backend binding (overrides the credential)',
    profile: 'from its access profile',
    credential: 'granted on the credential',
};
const CredentialPanel = ({ credentials }) => {
    if (!credentials || !credentials.length) return null;
    return (
        <div className="ilt-creds">
            {credentials.map((c, i) => {
                // Every module the record used on this credential (a list; one string from an older server).
                const needed = (Array.isArray(c.needed) ? c.needed : c.needed ? [c.needed] : []);
                const isNeeded = (path) => needed.includes(path);
                const missing = needed.filter(p => !c.modules.some(m => m.path === p));
                return (
                    <div className="ilt-cred" key={`${c.credential ? c.credential.id : 'x'}-${c.accessId || i}`}>
                        <div className="ilt-cred-head">
                            <span className="ilt-cred-label">Universal API credential</span>
                            <strong>{c.credential ? c.credential.name : 'unknown credential'}</strong>
                            {c.credential && <code className="ilt-cred-id">#{c.credential.id}</code>}
                            {c.xHotelId && <span className="ilt-muted">x-hotelid <code>{c.xHotelId}</code></span>}
                            {c.target && <span className="ilt-muted">→ {c.target}</span>}
                        </div>
                        <div className="ilt-cred-mods">
                            <span className="ilt-cred-label">
                                Modules {SOURCE_TEXT[c.source] || ''}
                                {c.source === 'profile' && (c.profileName ? ` “${c.profileName}”` : ' — none governs it, so nothing')}
                            </span>
                            {c.modules.length === 0 ? (
                                <span className="ilt-cred-none">none — every call is refused</span>
                            ) : c.modules.map(m => (
                                <span
                                    key={m.id}
                                    className={`ilt-cred-mod${isNeeded(m.path) ? ' is-needed' : ''}`}
                                    title={`${m.name || m.path}${m.partial ? ' — only some of its operations' : ''}`
                                        + `${isNeeded(m.path) ? ' — a module this message used' : ''}`}
                                >
                                    {m.path}{m.partial ? '*' : ''}
                                </span>
                            ))}
                            {missing.map(p => (
                                <span key={p} className="ilt-cred-missing">{p} needed — not on this credential</span>
                            ))}
                        </div>
                        {i === credentials.length - 1 && (
                            <p className="ilt-cred-note">
                                As granted now — grants are not versioned. <code>*</code> = only some operations.
                            </p>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// WHERE THE REASON IS FIXED, for the reasons that have one place: an implementation that is not active is
// switched on on its template's Implementations tab; a template that is offline, or a route that is off or
// missing, on the template itself (then published and made live).
const fixLinkOf = (g) => {
    const r = g.reason;
    if (!r || !g.integrationId) return null;
    const base = `/pipeline-templates/${g.integrationId}/edit?from=pipelines`;
    if (r.step === 'proxy.implementation') return { href: `${base}&tab=implementations`, label: 'Open the implementation →' };
    if (r.step === 'proxy.version' || r.step === 'proxy.route' || r.step === 'proxy.route.match') {
        return { href: base, label: 'Open the template →' };
    }
    return null;
};

// loadSteps(groupId) -> the record's steps, oldest first. Defaults to the superadmin integration log endpoint;
// Universal API > Logs passes its own, limited to its property and to the Universal API steps.
// The superadmin endpoint answers { steps, credentials } — the credentials the Universal API was called as,
// with what each may reach. A host's own loader may answer the steps alone.
const defaultLoadSteps = async (groupId) => integrationsService.getLogGroup(groupId);
const splitLoaded = (got) => (Array.isArray(got)
    ? { steps: got, credentials: [] }
    : { steps: (got && got.steps) || [], credentials: (got && got.credentials) || [] });

/**
 * The integration log as records — ONE PER MESSAGE, grouped on the server (steps sharing a transaction id).
 *
 * groups: [{ groupId, transactionId, latest, stepCount, outcome, outcomes, firstAt, lastAt, touchesUapi,
 *            integrationId, integrationName, messageType, property }]
 *
 * A record's steps are not in the list: the chevron fetches them (lazily, once) and shows the message's
 * round trip — request out, response back — each step opening in a dialog.
 */
// canResend: offer Resend on each record's board (the superadmin Pipelines log only — the Universal API log
// is a property's view and must not re-deliver anything).
// canAssist: Ask AI and Jira on each record (superadmin — both send the record to the support side).
// A POLL of the source (eventpoller poller.tick, POLL-<implementation>-<ms>) or a RESYNC run (resync.tick,
// RESYNC-<runId>) — a parent record that carries no message of its own; it lists the records it carried, each its own
// record. Nothing to replay on the parent itself.
const isResyncRecord = (g) => g.messageType === 'Resync' || String(g.transactionId || g.groupId || '').startsWith('RESYNC-');
// A SCHEDULED JOB run (scheduledjob.tick, SCHEDULE-<runId>, V710) — same shape as a resync: a parent record whose
// children are the records it pushed, each naming it via x-hc-poll-id.
const isScheduledRecord = (g) => g.messageType === 'Scheduled Job' || String(g.transactionId || g.groupId || '').startsWith('SCHEDULE-');
const isPollRecord = (g) => g.messageType === 'Poll' || isResyncRecord(g) || isScheduledRecord(g)
    || String(g.transactionId || g.groupId || '').startsWith('POLL-');

// WHAT CAUSED WHAT. OHIP names the action that caused an event (the header's parentActionId); an event whose cause is in
// the same list is put directly under it, indented, rather than beside it — depth-first, in the list's own order.
const causeOrder = (list) => {
    const byAction = new Map(list.filter(e => e.actionId != null).map(e => [String(e.actionId), e]));
    const parentOf = (e) => (e.parentActionId != null && byAction.get(String(e.parentActionId)) !== e ? byAction.get(String(e.parentActionId)) : null);
    const kids = new Map();
    for (const e of list) {
        const p = parentOf(e);
        if (p) kids.set(p, [...(kids.get(p) || []), e]);
    }
    const out = [];
    const seen = new Set();
    const walk = (e, depth) => {
        if (seen.has(e)) return;
        seen.add(e);
        out.push({ e, depth });
        for (const k of kids.get(e) || []) walk(k, depth + 1);
    };
    for (const e of list) if (!parentOf(e)) walk(e, 0);
    for (const e of list) walk(e, 0);   // a loop of causes, should OHIP ever send one, is still shown
    return out;
};

// A POLL CARRIES NO MESSAGE, so it is not painted on the pipeline (an empty board read as "nothing happened"). It lists
// the events it dequeued instead — each its own record, opened by its transaction on this same Logs tab.
const pollEventHref = (transactionId) => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', 'logs');
    url.searchParams.set('transactionId', transactionId);
    return `${url.pathname}?${url.searchParams.toString()}`;
};
// Each event opens IN PLACE (its chevron): `isOpen(e)`, `onToggle(e)` and `renderBody(e)` come from the table, which
// loads the event's own record and draws it as its row would be — credentials, Board / Log entries.
const PollDeliveries = ({ steps, onOpen, listLogs, events = [], isOpen = () => false, onToggle, renderBody }) => {
    const tick = steps.find(st => st.step === 'poller.tick' || st.step === 'resync.tick' || st.step === 'scheduledjob.tick')
        || steps.find(st => st.step === 'poller.start' || st.step === 'resync.start') || steps[0];
    // A RESYNC or a SCHEDULED JOB: its children are adapter records named by poll_id (passed in as `events`), not
    // poller.event steps — so the time-based poller.event lookup is skipped for both (it would pull a neighbour's events).
    const isResync = Boolean(tick && /^(resync|scheduledjob)/.test(String(tick.step || '')));
    const isScheduled = Boolean(tick && String(tick.step || '').startsWith('scheduledjob'));
    const d = (tick && detailOf(tick)) || {};
    // THE EVENTS' OWN RECORDS on this page (they name this poll) come first — live, with their latest outcome, and
    // known even while the poll still runs; else the list the poll wrote; else found by time (older polls).
    const fromRecords = events.length ? events.map(g => ({
        transactionId: g.transactionId || g.groupId, module: g.messageType, outcome: g.outcome,
        note: g.latest && g.latest.message, actionId: g.actionId, parentActionId: g.parentActionId,
    })) : null;
    const listed = fromRecords || (Array.isArray(d.deliveries) ? d.deliveries : null);
    // A POLL LOGGED BEFORE IT LISTED ITS EVENTS: they are found the way they were written — this hotel's poller.event
    // records in the seconds the poll ran (its duration, back from when it was logged).
    const [found, setFound] = useState(null);
    useEffect(() => {
        // Resync children are adapter records named by poll_id (passed in as `events`), not poller.event steps — so the
        // time-based poller.event lookup is for polls only; for a resync it would pull in a neighbouring poll's events.
        if (listed || isResync || !tick || !(d.events > 0) || !listLogs) return undefined;
        let live = true;
        const end = new Date(tick['@timestamp']).getTime();
        const start = end - (Number(tick.duration_ms) || 60000) - 5000;
        listLogs({ integrationId: tick.integration_id, hotelId: tick.hotel_id, step: 'poller.event',
            since: new Date(start).toISOString(), until: new Date(end + 2000).toISOString(), limit: 50 })
            .then(res => { if (live) setFound((res.groups || []).map(g => ({
                transactionId: g.transactionId || g.groupId, module: g.messageType, outcome: g.outcome,
                note: g.latest && g.latest.message, actionId: g.actionId, parentActionId: g.parentActionId,
            }))); })
            .catch(() => { if (live) setFound([]); });
        return () => { live = false; };
    }, [tick && tick.id]); // eslint-disable-line
    const list = listed || found || [];
    return (
        <div className="ilt-poll">
            {tick && (
                <button type="button" className={`ilt-boxstep ilt-outcome-${tick.outcome || 'unknown'}`} onClick={() => onOpen(tick)}>
                    <span className="ilt-dot" aria-hidden="true" />
                    <span className="ilt-boxstep-text">
                        <span className="ilt-boxstep-label">{labelOf(tick)}</span>
                        <span className="ilt-boxstep-msg">{tick.message}</span>
                    </span>
                    <span className="ilt-stage-step-time">{clock(tick['@timestamp'])}</span>
                </button>
            )}
            {list.length > 0 ? (
                <>
                    <div className="ilt-poll-head">{isScheduled ? 'Records this scheduled job pushed — each is its own record' : isResync ? 'Records this resync sent — each is its own record' : 'Events this poll dequeued — each is its own record'}</div>
                    <ul className="ilt-poll-list">
                        {causeOrder(list).map(({ e, depth }) => (
                            <li key={e.transactionId} className={`ilt-outcome-${e.outcome || 'unknown'}${isOpen(e) ? ' is-open' : ''}`}
                                style={depth ? { marginLeft: `${depth * 1.5}rem` } : undefined}>
                                <div className="ilt-poll-event">
                                    {onToggle && (
                                        <button type="button" className="hc-chevron-btn" aria-expanded={isOpen(e)}
                                            aria-label={isOpen(e) ? `Hide ${e.transactionId}` : `Show ${e.transactionId}`}
                                            onClick={() => onToggle(e)}>
                                            <ChevronRight size={14} strokeWidth={2.5} />
                                        </button>
                                    )}
                                    <span className="ilt-dot" aria-hidden="true" />
                                    {/* WHAT THE RECORD IS — its module and OHIP id (e.g. /rsv 562558201) — ALWAYS, then
                                        the note (a warning, a skip reason) as context. The note used to replace the
                                        identity, so a reservation whose rate summary was dropped read only the warning
                                        with no sign it was a /rsv or which one. */}
                                    <span className="ilt-poll-event-what">
                                        {[e.module, e.primaryKey || e.actionId].filter(Boolean).join(' ')}{e.actionType ? ` (${e.actionType})` : ''}
                                        {e.note && <span className="ilt-muted">{[e.module, e.primaryKey || e.actionId].filter(Boolean).length ? ' — ' : ''}{e.note}</span>}
                                    </span>
                                    {/* ASSOCIATED WITH WHAT CAUSED IT, said even when that event is not in this poll (OHIP sent it earlier). */}
                                    {e.parentActionId && (
                                        <span className="ilt-chip ilt-chip-sm ilt-poll-chip" title={`OHIP names action ${e.parentActionId} as what caused this event${depth ? ' — shown above it' : ''}`}>
                                            caused by action {e.parentActionId}
                                        </span>
                                    )}
                                    <a href={pollEventHref(e.transactionId)} title="Open this event as its own record"><code>{e.transactionId}</code> →</a>
                                </div>
                                {isOpen(e) && renderBody && <div className="ilt-poll-event-body">{renderBody(e)}</div>}
                            </li>
                        ))}
                    </ul>
                    {d.events > list.length && <div className="ilt-poll-head">and {d.events - list.length} more — filter by time to see them</div>}
                </>
            ) : d.events > 0 ? (
                <div className="ilt-poll-head">{!listed && found === null
                    ? (isScheduled ? 'Finding the records this scheduled job pushed…' : isResync ? 'Finding the records this resync sent…' : 'Finding the events this poll carried…')
                    : `This ${isScheduled ? 'scheduled job pushed' : isResync ? 'resync sent' : 'poll carried'} ${d.events} ${isResync ? 'record' : 'event'}${d.events === 1 ? '' : 's'}; each is its own record in this log, at the same time.`}</div>
            ) : null}
        </div>
    );
};

// listLogs: the list read this table's records came from (a poll looks up the events it carried with it).
const IntegrationLogTable = ({ groups, showIntegration = true, emptyText = 'Nothing logged.', loadSteps = defaultLoadSteps, canResend = false, canAssist = true, listLogs = null, selectable = false, onBulkResync = null }) => {
    const [openKey, setOpenKey] = useState(null);
    const [steps, setSteps] = useState({});        // groupId -> steps | null (failed)
    const [creds, setCreds] = useState({});        // groupId -> [credential entitlement]
    // BOARD OR STEPS, per record. The board shows where each step happened; the list shows them in order
    // with their sentences — the quicker read for a record with many steps in one box.
    const [views, setViews] = useState({});        // groupId -> 'board' | 'steps'
    // ASK AI — the record Claude is being asked about (logs.js explain builds the context server-side).
    const [askFor, setAskFor] = useState(null);
    const [graphs, setGraphs] = useState({});      // integrationId -> design | null (not available)
    const [loadingKey, setLoadingKey] = useState(null);
    const [dialogStep, setDialogStep] = useState(null);
    // RAISING A TICKET FROM A RECORD. The server builds it from the record itself — see logs.js — so the
    // button sends the id and an optional note, and remembers the ticket it got back so the row can link to
    // it instead of offering to raise a second one.
    const [jiraFor, setJiraFor] = useState(null);      // the record a ticket is being raised for
    const [jiraNote, setJiraNote] = useState('');
    const [jiraBusy, setJiraBusy] = useState(false);
    const [jiraError, setJiraError] = useState(null);
    const [tickets, setTickets] = useState({});        // groupId -> { key, url }

    const raiseJira = async () => {
        if (!jiraFor || jiraBusy) return;
        setJiraBusy(true);
        setJiraError(null);
        try {
            const created = await integrationsService.raiseLogJira({
                groupId: jiraFor.groupId,
                transactionId: jiraFor.transactionId,
                note: jiraNote.trim() || undefined,
                pageUrl: window.location.href,
            });
            setTickets(prev => ({ ...prev, [jiraFor.groupId]: created }));
            setJiraFor(null);
        } catch (e) {
            setJiraError(e.response?.data?.error?.message || e.response?.data?.message
                || e.message || 'The ticket could not be created');
        } finally {
            setJiraBusy(false);
        }
    };

    // THE DESIGN THE RECORD IS PAINTED ON, fetched once per integration while a list of records is on
    // screen: every record of the same integration is the same board, and re-fetching it per row would be
    // a request per chevron.
    //
    // HELD ONLY AS LONG AS THE LIST IS. A design is edited on the next screen along — a route renamed, a
    // module added — and a board cached across that showed the OLD picture, with the record's routes
    // matching nothing on it. Refreshing the log now refreshes the design with it.
    //
    // A design that cannot be read (deleted, or a reader without access to it) is remembered as null and the
    // record falls back to a plain list — a log that will not open because its picture is missing is worse
    // than a log without a picture.
    useEffect(() => { setGraphs({}); }, [groups]);

    const loadGraph = async (integrationId) => {
        if (!integrationId || graphs[integrationId] !== undefined) return;
        try {
            const g = await integrationsService.getGraph(integrationId);
            setGraphs(prev => ({ ...prev, [integrationId]: g }));
        } catch (e) {
            setGraphs(prev => ({ ...prev, [integrationId]: null }));
        }
    };

    // RESYNC FROM OHIP. Unlike a replay of the stored original, this re-queries OHIP for
    // the record by its id and runs the CURRENT version through the adapter — so a delivery that failed (e.g. the
    // Universal API was throttling) is retried against live data. One failed message, or every record a batch failed
    // on (batch: true, groupId = the Poll / Resync parent). Each resync runs as its own "Resync" record on this log.
    const [resyncFor, setResyncFor] = useState(null);   // { g, batch } awaiting confirmation
    const [resyncing, setResyncing] = useState(null);   // groupId in flight
    const [resyncNote, setResyncNote] = useState({});   // groupId -> { ok, text }

    // SELECTION — pick failed rows across the whole page (across hotels) and resync them together. The selection carries
    // each row's { groupId, batch }; the parent opens the bulk resync modal with them (onBulkResync), which resolves and
    // starts the runs on the server and shows per-hotel progress. The same { batch } each row's own Resync button uses.
    const [selected, setSelected] = useState({});       // groupId -> { g, batch }

    // The server's answer to one resync, as a note for the record's body. Shared by the per-row button and the bulk run
    // so both read identically. Returns { ok, text }: ok is whether a run actually started.
    const resyncResultNote = (batch, res) => {
        if (batch) {
            const started = (res.runs || []).filter(r => r.runId);
            if (!started.length) {
                const f = (res.runs || []).find(r => r.error);
                return { ok: false, text: f ? f.error : 'Nothing was resynced.' };
            }
            return { ok: true, text: `Re-querying OHIP for ${res.records} failed record${res.records === 1 ? '' : 's'} — `
                + `${started.length} resync run${started.length === 1 ? '' : 's'} started. Each record is re-read from OHIP `
                + 'and sent again, appearing on this log as its own “Resync”.' };
        }
        return { ok: true, text: `Re-querying OHIP for ${res.module} ${res.recordId} — a resync has started, and runs as its own “Resync” record on this log.` };
    };
    const resyncErrorText = (e) => e.response?.data?.error?.message || e.response?.data?.message || e.message || 'The resync could not be started';

    // Start one resync (single message or whole batch) and record its note on the row. Returns true when a run started.
    const startResync = async (g, batch) => {
        try {
            const res = batch
                ? await integrationsService.resyncLogBatch(g.groupId)
                : await integrationsService.resyncLogMessage(g.groupId);
            const note = resyncResultNote(batch, res);
            setResyncNote(prev => ({ ...prev, [g.groupId]: note }));
            return note.ok;
        } catch (e) {
            setResyncNote(prev => ({ ...prev, [g.groupId]: { ok: false, text: resyncErrorText(e) } }));
            return false;
        }
    };

    const runResync = async () => {
        const ask = resyncFor;
        setResyncFor(null);
        if (!ask) return;
        setResyncing(ask.g.groupId);
        try { await startResync(ask.g, ask.batch); setOpenKey(ask.g.groupId); }
        finally { setResyncing(null); }
    };

    // Hand the selected rows to the parent's bulk resync modal (as { groupId, batch }) and clear the selection.
    const resyncSelected = () => {
        const items = Object.values(selected).map(({ g, batch }) => ({ groupId: g.groupId, batch }));
        if (!items.length || !onBulkResync) return;
        onBulkResync(items);
        setSelected({});
    };

    const toggle = async (g) => {
        if (openKey === g.groupId) { setOpenKey(null); return; }
        setOpenKey(g.groupId);
        loadGraph(g.integrationId);
        if (steps[g.groupId] !== undefined) return;
        try {
            setLoadingKey(g.groupId);
            const got = splitLoaded(await loadSteps(g.groupId));
            setSteps(prev => ({ ...prev, [g.groupId]: got.steps }));
            setCreds(prev => ({ ...prev, [g.groupId]: got.credentials }));
        } catch (e) {
            setSteps(prev => ({ ...prev, [g.groupId]: null }));
        } finally {
            setLoadingKey(null);
        }
    };

    // ONE RECORD'S BODY — its credentials, the Board / Log entries toggle and the board itself. The same for a row of
    // the list and for an event opened inside the poll that dequeued it.
    const recordBody = (g) => {
        const loaded = steps[g.groupId];
        return (
            (loadingKey === g.groupId || loaded === undefined) ? <Spinner /> : loaded === null ? (
                <p className="ilt-muted">This record's steps could not be loaded.</p>
            ) : (
                <>
                    {resyncNote[g.groupId] && (
                        <div className={`os-msg ${resyncNote[g.groupId].ok ? 'os-msg-ok' : 'os-msg-error'} ilt-resend-note`}>
                            {resyncNote[g.groupId].text}
                        </div>
                    )}
                    <CredentialPanel credentials={creds[g.groupId] || []} />
                    {isPollRecord(g) ? (
                        <PollDeliveries
                            steps={loaded || []}
                            onOpen={setDialogStep}
                            listLogs={listLogs}
                            events={childrenOf.get(g.groupId) || []}
                            isOpen={(e) => !!openEvents[e.transactionId]}
                            onToggle={(e) => toggleEvent(eventGroup(e, g))}
                            renderBody={(e) => recordBody(eventGroup(e, g))}
                        />
                    ) : <>
                    {graphs[g.integrationId] && (
                        <div className="ilt-view-toggle" role="radiogroup" aria-label="How to show this record">
                            {[['board', 'Board', LayoutGrid], ['steps', 'Log entries', List]].map(([v, label, Icon]) => (
                                <button
                                    key={v}
                                    type="button"
                                    role="radio"
                                    aria-checked={(views[g.groupId] || 'board') === v}
                                    className={`ilt-view-btn${(views[g.groupId] || 'board') === v ? ' is-on' : ''}`}
                                    onClick={() => setViews(prev => ({ ...prev, [g.groupId]: v }))}
                                >
                                    <Icon size={12} /> {label}
                                </button>
                            ))}
                        </div>
                    )}
                    <RecordBoard
                        view={views[g.groupId] || 'board'}
                        steps={loaded || []}
                        graph={graphs[g.integrationId] || null}
                        onOpen={setDialogStep}
                    />
                    </>}
                </>
            )
        );
    };

    // AN EVENT OPENED INSIDE ITS POLL: loaded by its own transaction, shown with the same body as its own row would be.
    const [openEvents, setOpenEvents] = useState({});   // transaction id -> open
    const eventGroup = (e, poll) => ({
        groupId: e.transactionId, transactionId: e.transactionId, integrationId: poll.integrationId,
        messageType: e.module, property: poll.property, outcome: e.outcome,
    });
    const toggleEvent = async (eg) => {
        const key = eg.groupId;
        const opening = !openEvents[key];
        setOpenEvents(prev => ({ ...prev, [key]: opening }));
        if (!opening) return;
        loadGraph(eg.integrationId);
        if (steps[key] !== undefined) return;
        try {
            const got = splitLoaded(await loadSteps(key));
            setSteps(prev => ({ ...prev, [key]: got.steps }));
            setCreds(prev => ({ ...prev, [key]: got.credentials }));
        } catch (e) {
            setSteps(prev => ({ ...prev, [key]: null }));
        }
    };

    // THE EVENTS A POLL DEQUEUED, GROUPED UNDER IT. Each event stays a record of its own (its transaction joins the
    // adapter, the Universal API and the target), and is opened inside the poll that brought it in. A poll names its
    // events (detail.deliveries, by transaction); one logged before it did is matched by time — this hotel's
    // poller.event records from the seconds the poll ran. An event whose poll is not on this page stands alone.
    const { childrenOf, childIds, syntheticBatches } = (() => {
        const map = new Map();
        const taken = new Set();
        const parentIds = new Set(groups.filter(isPollRecord).map(p => String(p.transactionId || p.groupId)));
        for (const p of groups.filter(isPollRecord)) {
            const d = detailOf(p.latest) || {};
            const ids = Array.isArray(d.deliveries) ? new Set(d.deliveries.map(x => String(x.transactionId))) : null;
            const end = Date.parse(p.lastAt);
            const start = end - (Number(p.latest.duration_ms) || 60000) - 5000;
            const kids = groups.filter(g => !isPollRecord(g) && !taken.has(g.groupId) && (g.pollId
                // Every step of a polled event names its poll (poll_id) — exact, and true while the poll still runs.
                ? String(g.pollId) === String(p.transactionId || p.groupId)
                : ids
                ? ids.has(String(g.transactionId || g.groupId))
                : g.latest.step === 'poller.event' && String(g.latest.hotel_id) === String(p.latest.hotel_id)
                    && String(g.integrationId) === String(p.integrationId)
                    && Date.parse(g.firstAt) >= start && Date.parse(g.firstAt) <= end + 2000));
            kids.sort((a, b) => Date.parse(a.firstAt) - Date.parse(b.firstAt));
            kids.forEach(k => taken.add(k.groupId));
            if (kids.length) map.set(p.groupId, kids);
        }

        // A RECORD THAT NAMES A BATCH WHOSE PARENT ISN'T ON THIS PAGE (poll_id set, but no Poll/Resync/Scheduled
        // record with that transaction in view — it was filtered out, or paged off) would otherwise show as a loose
        // row, so a failure read as standalone instead of "inside the batch it occurred in". Synthesize a batch
        // header per such poll_id and nest its records under it; the header opens like any batch — its real
        // RESYNC-/POLL-/SCHEDULE- record is fetched by its id (loadSteps) when expanded.
        const orphansByPoll = new Map();
        for (const g of groups) {
            if (isPollRecord(g) || taken.has(g.groupId) || !g.pollId || parentIds.has(String(g.pollId))) continue;
            const pid = String(g.pollId);
            if (!orphansByPoll.has(pid)) orphansByPoll.set(pid, []);
            orphansByPoll.get(pid).push(g);
        }
        const rank = { error: 4, denied: 3, warning: 2, ok: 1, skipped: 0 };
        const syntheticBatches = [];
        for (const [pid, kids] of orphansByPoll) {
            kids.sort((a, b) => Date.parse(a.firstAt) - Date.parse(b.firstAt));
            kids.forEach(k => taken.add(k.groupId));
            map.set(pid, kids);
            const lastAt = kids.reduce((m, k) => (Date.parse(k.lastAt) > Date.parse(m) ? k.lastAt : m), kids[0].lastAt);
            const worst = kids.reduce((w, k) => ((rank[k.outcome] || 0) > (rank[w] || 0) ? k.outcome : w), 'ok');
            const step = pid.startsWith('SCHEDULE-') ? 'scheduledjob.tick' : pid.startsWith('RESYNC-') ? 'resync.tick' : 'poller.tick';
            syntheticBatches.push({
                groupId: pid, transactionId: pid, synthetic: true,
                messageType: pid.startsWith('SCHEDULE-') ? 'Scheduled Job' : pid.startsWith('RESYNC-') ? 'Resync' : 'Poll',
                outcome: worst, lastAt, firstAt: kids[0].firstAt, stepCount: kids.length,
                integrationId: kids[0].integrationId, integrationName: kids[0].integrationName,
                latest: { step, message: `${kids.length} record${kids.length === 1 ? '' : 's'} in this batch`, '@timestamp': lastAt },
            });
        }
        return { childrenOf: map, childIds: taken, syntheticBatches };
    })();
    // The events live INSIDE their poll (opened there, each with its chevron), so they are not repeated as rows.
    // Synthesized batch headers take their place as a row, slotted back into the list by time.
    const ordered = [...groups.filter(g => !childIds.has(g.groupId)), ...syntheticBatches]
        .sort((a, b) => Date.parse(b.lastAt) - Date.parse(a.lastAt));

    // How many records a batch (Poll / Resync / Scheduled parent) failed on — the count the "Resync all failed"
    // button offers to re-query. From the children the table grouped under it, by their own outcome.
    const batchFailedCount = (g) => (childrenOf.get(g.groupId) || []).filter(e => e.outcome === 'error').length;

    // Whether and how a row can be resynced — the same two cases the per-row Resync buttons offer. A single failed
    // message (batch:false), or a batch with failed children (batch:true). null when the row has nothing to resync, so
    // only resyncable rows get a checkbox. A row with no transaction/record id to re-query cannot be resynced either.
    const resyncTargetOf = (g) => {
        if (!canResend) return null;
        if (!isPollRecord(g) && g.outcome !== 'ok') return { g, batch: false };
        if (isPollRecord(g) && batchFailedCount(g) > 0) return { g, batch: true };
        return null;
    };
    const selectableRows = selectable ? ordered.map(resyncTargetOf).filter(Boolean) : [];
    const selectedCount = Object.keys(selected).length;
    const allSelected = selectableRows.length > 0 && selectableRows.every(t => selected[t.g.groupId]);
    const toggleRow = (t) => setSelected(prev => {
        const next = { ...prev };
        if (next[t.g.groupId]) delete next[t.g.groupId]; else next[t.g.groupId] = t;
        return next;
    });
    const toggleAll = () => setSelected(prev => {
        if (allSelected) {
            const next = { ...prev };
            selectableRows.forEach(t => { delete next[t.g.groupId]; });
            return next;
        }
        const next = { ...prev };
        selectableRows.forEach(t => { next[t.g.groupId] = t; });
        return next;
    });
    const colCount = (showIntegration ? 7 : 6) + (selectable ? 1 : 0);

    if (!groups.length) return <div className="ilt-empty">{emptyText}</div>;

    return (
        <div className="ilt-wrap">
            {/* BULK RESYNC BAR — the selected failed rows (across hotels), resynced together. Only when rows are selected. */}
            {selectable && selectedCount > 0 && (
                <div className="ilt-bulkbar">
                    <span className="ilt-bulkbar-count">{selectedCount} selected</span>
                    <button type="button" className="btn btn-primary btn-sm" onClick={resyncSelected}>
                        <RefreshCw size={13} style={{ marginRight: 6, verticalAlign: '-2px' }} />
                        Resync {selectedCount} selected
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setSelected({})}>Clear</button>
                </div>
            )}
            <table className="ilt-table">
                <thead>
                    <tr>
                        {/* Widths for the fixed layout (IntegrationLogTable.css); What happened takes the rest. */}
                        {selectable && (
                            <th aria-label="Select" style={{ width: 30 }}>
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    ref={el => { if (el) el.indeterminate = !allSelected && selectedCount > 0; }}
                                    onChange={toggleAll}
                                    disabled={selectableRows.length === 0}
                                    title={allSelected ? 'Clear selection' : 'Select every resyncable row shown'}
                                />
                            </th>
                        )}
                        <th aria-label="Expand" style={{ width: 28 }} />
                        <th style={{ width: '9rem' }}>When</th>
                        {showIntegration && <th style={{ width: '9rem' }}>Integration</th>}
                        <th style={{ width: '15rem' }}>Latest step</th>
                        <th style={{ width: '6.5rem' }}>Outcome</th>
                        <th>What happened</th>
                        <th style={{ width: '13rem' }}>Message</th>
                    </tr>
                </thead>
                <tbody>
                    {ordered.map(g => {
                        const kids = (childrenOf.get(g.groupId) || []).length;
                        const open = openKey === g.groupId;
                        const txn = g.transactionId;
                        const target = selectable ? resyncTargetOf(g) : null;
                        return (
                            <React.Fragment key={g.groupId}>
                                <tr
                                    className={`ilt-row ${open ? 'is-open' : ''}${kids ? ' ilt-row-parent' : ''}`}
                                    onClick={() => toggle(g)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(g); } }}
                                    tabIndex={0}
                                    aria-expanded={open}
                                >
                                    {selectable && (
                                        <td onClick={e => e.stopPropagation()}>
                                            {target && (
                                                <input
                                                    type="checkbox"
                                                    checked={!!selected[g.groupId]}
                                                    onChange={() => toggleRow(target)}
                                                    title={target.batch ? `Select — resync ${batchFailedCount(g)} failed` : 'Select this record to resync'}
                                                />
                                            )}
                                        </td>
                                    )}
                                    <td>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                                    {/* DATE OVER TIME — the whole stamp on one line ran past the column into the next. */}
                                    <td className="ilt-when">{(() => {
                                        const [day, ...time] = formatDateTime(g.lastAt).split(' ');
                                        return <><div>{day}</div>{time.length > 0 && <div className="ilt-when-time">{time.join(' ')}</div>}</>;
                                    })()}</td>
                                    {showIntegration && (
                                        <td>{g.integrationName || (g.integrationId ? `#${g.integrationId}` : <span className="ilt-muted">none</span>)}</td>
                                    )}
                                    <td>
                                        <span className="ilt-step" title={g.latest.step}>{stepLabel(g.latest.step)}</span>
                                        <div className="ilt-count">
                                            {g.stepCount} step{g.stepCount === 1 ? '' : 's'}
                                            {kids > 0 && <span className="ilt-chip ilt-chip-sm ilt-poll-chip">{kids} event{kids === 1 ? '' : 's'}</span>}
                                            {g.routeName && <span className="ilt-route-chip" title={`Taken by ${g.routeName}`}>{g.routeName}</span>}
                                            {g.touchesUapi && <span className="ilt-chip ilt-uapi ilt-chip-sm">Universal API</span>}
                                        </div>
                                    </td>
                                    <td><Outcome outcome={g.outcome} /></td>
                                    <td className="ilt-text">
                                        {/* THE REASON, NOT THE LAST WORD. A record that did not get through says
                                            why (the server picks the step that decided it); one that did says
                                            what happened last. */}
                                        {g.outcome !== 'ok' && g.reason ? g.reason.message : g.latest.message}
                                        {g.outcome !== 'ok' && fixLinkOf(g) && (
                                            <div onClick={e => e.stopPropagation()}>
                                                <a className="ilt-fix" href={fixLinkOf(g).href}>{fixLinkOf(g).label}</a>
                                            </div>
                                        )}
                                    </td>
                                    <td onClick={e => e.stopPropagation()}>
                                        {g.messageType && <div className="ilt-type">{g.messageType}{g.property ? ` · ${g.property}` : ''}</div>}
                                        {txn && (
                                            <div className="ilt-txn">
                                                <code title={txn}>{txn.length > 22 ? `…${txn.slice(-20)}` : txn}</code>
                                                <CopyButton value={txn} label="" copiedLabel="" title="Copy the transaction id" />
                                            </div>
                                        )}
                                        {/* RESYNC FROM OHIP, ON A FAILED MESSAGE — re-query OHIP for this record and
                                            process it again (logs.js resync). The live version, not the stored original,
                                            so a delivery that failed on throttling or a transient fault is retried
                                            against current data. Shown where the record did not get through. */}
                                        {canResend && !isPollRecord(g) && g.outcome !== 'ok' && (
                                            <button
                                                type="button"
                                                className="ilt-jira ilt-replay"
                                                onClick={() => setResyncFor({ g, batch: false })}
                                                disabled={resyncing === g.groupId}
                                                title="Re-query OHIP for this record and process it through the pipeline again"
                                            >
                                                <RefreshCw size={11} /> {resyncing === g.groupId ? 'Resyncing…' : 'Resync'}
                                            </button>
                                        )}
                                        {/* RESYNC ALL FAILED, ON A BATCH — a Poll / Resync / Scheduled parent that has
                                            failed children: re-query OHIP for every record it failed on and process
                                            them again, as one Resync run per module. */}
                                        {canResend && isPollRecord(g) && batchFailedCount(g) > 0 && (
                                            <button
                                                type="button"
                                                className="ilt-jira ilt-replay"
                                                onClick={() => setResyncFor({ g, batch: true })}
                                                disabled={resyncing === g.groupId}
                                                title="Re-query OHIP for every record this batch failed on and process them again"
                                            >
                                                <RefreshCw size={11} /> {resyncing === g.groupId ? 'Resyncing…' : `Resync ${batchFailedCount(g)} failed`}
                                            </button>
                                        )}
                                        {/* ASK AI — what happened to this message, and how to fix it. Only where the
                                            superadmin log is the source: the server re-reads the record. */}
                                        {canAssist && loadSteps === defaultLoadSteps && (
                                            <button
                                                type="button"
                                                className="ilt-jira ilt-ask"
                                                onClick={() => setAskFor(g)}
                                                title="Ask Claude what happened to this message and how to fix it"
                                            >
                                                <Sparkles size={11} /> Ask AI?
                                            </button>
                                        )}
                                        {/* A TICKET FROM THE RECORD. Everything a fix needs is already here —
                                            the routes weighed up, every step, the bodies — and it is reported
                                            today by pasting a screenshot, which carries none of it. */}
                                        {!canAssist ? null : tickets[g.groupId] ? (
                                            <a
                                                className="ilt-jira is-raised"
                                                href={tickets[g.groupId].url}
                                                target="_blank"
                                                rel="noreferrer"
                                                title="Open the Jira raised from this record"
                                            >
                                                <ExternalLink size={11} /> {tickets[g.groupId].key}
                                            </a>
                                        ) : (
                                            <button
                                                type="button"
                                                className="ilt-jira"
                                                onClick={() => { setJiraNote(''); setJiraError(null); setJiraFor(g); }}
                                                title="Raise a Jira with this record's steps, routes and bodies"
                                            >
                                                <Bug size={11} /> Jira
                                            </button>
                                        )}
                                    </td>
                                </tr>
                                {open && (
                                    <tr className="ilt-expand">
                                        <td colSpan={colCount}>
                                            {recordBody(g)}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
            {dialogStep && <StepDialog step={dialogStep} onClose={() => setDialogStep(null)} />}

            {askFor && (
                <div className="modal-overlay" onClick={() => setAskFor(null)}>
                    <div className="modal-content modal-lg ilt-ask-dialog" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="ilt-ask-title">
                        <div className="modal-header">
                            <h2 id="ilt-ask-title">
                                Ask AI — {askFor.messageType || 'message'}{askFor.property ? ` · ${askFor.property}` : ''}
                                {' '}<Outcome outcome={askFor.outcome} />
                            </h2>
                            <button className="modal-close" onClick={() => setAskFor(null)} aria-label="Close"><X size={16} /></button>
                        </div>
                        <div className="modal-body">
                            <CustomCodeAssistant
                                key={askFor.groupId}
                                title="Log Assistant"
                                note="sees every step, the redacted bodies, the credential and the routes"
                                snippetContext={{ groupId: askFor.groupId }}
                                assist={integrationsService.explainLog}
                                initialQuestion="What happened to this message, and how do I fix it?"
                                suggestions={[
                                    'Which step decided the outcome?',
                                    'Which screen do I change to fix it?',
                                    'Would a resend work now?',
                                ]}
                                emptyText="Ask about this message."
                                placeholder="Ask about this message… (Enter to send, Shift+Enter for a new line)"
                            />
                        </div>
                    </div>
                </div>
            )}

            {resyncFor && (
                <ConfirmModal
                    title={resyncFor.batch ? 'Resync all failed records?' : 'Resync this message from OHIP?'}
                    message={resyncFor.batch
                        ? `Every record this ${isResyncRecord(resyncFor.g) ? 'resync' : isScheduledRecord(resyncFor.g) ? 'scheduled job' : 'poll'} `
                            + `failed on (${batchFailedCount(resyncFor.g)}) is re-queried from OHIP and sent through the pipeline again, to live `
                            + 'targets. Each is read fresh from OHIP — not a replay of what arrived before — and runs as its own “Resync” record.'
                        : `The ${resyncFor.g.messageType || 'record'}${resyncFor.g.property ? ` for ${resyncFor.g.property}` : ''} is re-queried `
                            + 'from OHIP by its id and sent through the pipeline again, to live targets. It is read fresh from OHIP — not a replay '
                            + 'of what arrived before — and runs as its own “Resync” record.'}
                    confirmLabel={resyncFor.batch ? 'Resync all failed' : 'Resync'}
                    onConfirm={runResync}
                    onCancel={() => setResyncFor(null)}
                />
            )}

            {jiraFor && (
                <ConfirmModal
                    title="Raise a Jira for this record"
                    message={'The ticket carries what happened, every route the proxy considered, every step '
                        + 'with its timing, and the redacted bodies — so the fix can reproduce it.'}
                    confirmLabel={jiraBusy ? 'Creating…' : 'Create ticket'}
                    onConfirm={raiseJira}
                    onCancel={() => setJiraFor(null)}
                >
                    <textarea
                        className="cm-textarea"
                        value={jiraNote}
                        onChange={e => { setJiraNote(e.target.value); setJiraError(null); }}
                        placeholder="What were you expecting to happen? (optional)"
                    />
                    {jiraError && <div className="error-banner">{jiraError}</div>}
                </ConfirmModal>
            )}
        </div>
    );
};

export default IntegrationLogTable;
